import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionPlanApi, SubscriptionPlan } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getPlanPricing, toDisplayNumber } from '../../utils/planPricing';

const isPubliclyOfferable = (plan: SubscriptionPlan): boolean => !plan.is_admin_only;

export interface YearlySavings {
  amount: number;
  percent: number;
}

export const usePricingData = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    subscriptionPlanApi
      .getAll()
      .then((response) => {
        if (cancelled) return;
        const publicPlans = response.data.plans
          .filter(isPubliclyOfferable)
          .sort((a, b) => a.sort_order - b.sort_order);
        setPlans(publicPlans);
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger les tarifs pour le moment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChoosePlan = (plan: SubscriptionPlan) => {
    if (isAuthenticated) {
      navigate('/account', { state: { activeTab: 'subscription' } });
      return;
    }
    navigate(`/register?plan=${encodeURIComponent(plan.name)}`);
  };

  const ctaLabel = (plan: SubscriptionPlan): string =>
    isAuthenticated ? 'Gérer mon abonnement' : plan.has_ads ? 'Commencer gratuitement' : 'Choisir ce plan';

  const premiumMonthlyPlan = plans.find((p) => !p.has_ads && getPlanPricing(p).billingPeriod === 'monthly');
  const premiumYearlyPlan = plans.find((p) => !p.has_ads && getPlanPricing(p).billingPeriod === 'yearly');

  const yearlySavings: YearlySavings | null = (() => {
    if (!premiumMonthlyPlan || !premiumYearlyPlan) return null;
    const monthlyTotal = toDisplayNumber(premiumMonthlyPlan.price_monthly) * 12;
    const yearlyPrice = toDisplayNumber(premiumYearlyPlan.price_yearly);
    if (monthlyTotal <= 0 || yearlyPrice <= 0 || yearlyPrice >= monthlyTotal) return null;
    const amount = monthlyTotal - yearlyPrice;
    const percent = Math.round((amount / monthlyTotal) * 100);
    return { amount, percent };
  })();

  return {
    plans,
    loading,
    error,
    isAuthenticated,
    premiumMonthlyPlan,
    premiumYearlyPlan,
    yearlySavings,
    handleChoosePlan,
    ctaLabel,
  };
};
